import React, { useState, useMemo, useEffect } from 'react';
import { SquarePen, CircleHelp, Lock } from 'lucide-react';
import '../../../../styles/resume-editor.css';

interface CustomSection {
  id: string;
  title: string;
  layout: 'inline' | 'stacked';
}

interface RegularSection {
  id: string;
  title: string;
}

interface CVStructure {
  uid: string;
  structure_type: string;
  structure_name: string;
  index: number;
}

interface SectionOrderTitlesProps {
  structures?: CVStructure[];
  regularSections?: RegularSection[];
  customSections?: CustomSection[];
  cvUid?: string;
  onUpdateSectionTitle?: (sectionUid: string, newTitle: string) => void;
  onUpdateRegularSectionTitle?: (id: string, title: string) => void;
  onReorderRegularSections?: (newOrder: RegularSection[]) => void;
  onUpdateCustomSectionTitle?: (id: string, title: string) => void;
  onReorderCustomSections?: (newOrder: CustomSection[]) => void;
  onReorderSections?: (sections: Array<{ section_uid: string; index: number }>) => Promise<void>;
}

interface Section {
  id: string;
  title: string;
  locked: boolean;
  isCustom?: boolean;
  uid?: string;
  index?: number;
}

export const SectionOrderTitles: React.FC<SectionOrderTitlesProps> = ({
  structures = [],
  regularSections,
  customSections = [],
  cvUid,
  onUpdateSectionTitle,
  onUpdateRegularSectionTitle,
  onReorderRegularSections,
  onUpdateCustomSectionTitle,
  onReorderCustomSections,
  onReorderSections,
}) => {
  const [lockedSections] = useState<Section[]>([
    { id: 'contact', title: 'Contact', locked: true },
    { id: 'links', title: 'Links', locked: true },
  ]);

  const structureTypeToId: Record<string, string> = {
    'TYPE_INFO': 'about',
    'TYPE_WORK_EXPERIENCE': 'experience',
    'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
    'TYPE_EDUCATION': 'education',
    'TYPE_CERTIFICATIONS': 'certifications',
    'TYPE_PROJECTS': 'projects',
    'TYPE_SKILLS': 'skills',
  };

  const sectionsFromStructures = useMemo(() => {
    const sortedStructures = [...structures].sort((a, b) => (a.index || 0) - (b.index || 0));
    return sortedStructures
      .filter(s => structureTypeToId[s.structure_type])
      .map((s) => ({
        id: structureTypeToId[s.structure_type],
        title: s.structure_name,
        locked: false,
        uid: s.uid,
        index: s.index || 0,
      }));
  }, [structures]);

  const defaultRegularSections: Section[] = sectionsFromStructures.length > 0
    ? sectionsFromStructures
    : [
        { id: 'about', title: 'About', locked: false },
        { id: 'experience', title: 'Experience', locked: false },
        { id: 'volunteer', title: 'Volunteer Experience', locked: false },
        { id: 'education', title: 'Education', locked: false },
        { id: 'certifications', title: 'Certifications', locked: false },
        { id: 'projects', title: 'Projects', locked: false },
        { id: 'skills', title: 'Skills', locked: false },
      ];

  const [unlockedSections, setUnlockedSections] = useState<Section[]>(
    sectionsFromStructures.length > 0
      ? sectionsFromStructures
      : regularSections 
        ? regularSections.map(rs => ({ id: rs.id, title: rs.title, locked: false }))
        : defaultRegularSections
  );

  useEffect(() => {
    if (sectionsFromStructures.length > 0) {
      setUnlockedSections(sectionsFromStructures);
    }
  }, [sectionsFromStructures]);

  const allUnlockedSections = useMemo(() => {
    const sectionsMap = new Map<string, Section>();
    
    sectionsFromStructures.forEach(s => {
      sectionsMap.set(s.id, s);
    });
    
    customSections.forEach(cs => {
      sectionsMap.set(cs.id, {
        id: cs.id,
        title: cs.title,
        locked: false,
        isCustom: true,
      });
    });
    
    const orderedSections: Section[] = [];
    
    sectionsFromStructures.forEach(s => {
      const section = sectionsMap.get(s.id);
      if (section) {
        orderedSections.push(section);
      }
    });
    
    customSections.forEach(cs => {
      const section = sectionsMap.get(cs.id);
      if (section) {
        orderedSections.push(section);
      }
    });
    
    return orderedSections;
  }, [sectionsFromStructures, customSections]);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState<string>('');
  const [localSectionOrder, setLocalSectionOrder] = useState<Section[] | null>(null);

  const displaySections = useMemo(() => {
    if (localSectionOrder !== null) {
      return localSectionOrder;
    }
    return allUnlockedSections;
  }, [localSectionOrder, allUnlockedSections]);

  useEffect(() => {
    setLocalSectionOrder(null);
  }, [sectionsFromStructures, customSections]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newSections = [...displaySections];
    const [removed] = newSections.splice(draggedIndex, 1);
    newSections.splice(dropIndex, 0, removed);
    
    setLocalSectionOrder(newSections);
    
    const regularSectionsReordered: Section[] = [];
    const customSectionsReordered: CustomSection[] = [];
    
    newSections.forEach(section => {
      if (section.isCustom) {
        const customSection = customSections.find(cs => cs.id === section.id);
        if (customSection) {
          customSectionsReordered.push(customSection);
        }
      } else {
        regularSectionsReordered.push(section);
      }
    });

    setUnlockedSections(regularSectionsReordered);
    
    if (onReorderRegularSections && regularSectionsReordered.length > 0) {
      onReorderRegularSections(regularSectionsReordered.map(rs => ({
        id: rs.id,
        title: rs.title,
      })));
    }
    
    if (onReorderCustomSections && customSectionsReordered.length > 0) {
      onReorderCustomSections(customSectionsReordered);
    }

    if (onReorderSections && cvUid) {
      const structureTypeToId: Record<string, string> = {
        'TYPE_INFO': 'about',
        'TYPE_WORK_EXPERIENCE': 'experience',
        'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
        'TYPE_EDUCATION': 'education',
        'TYPE_CERTIFICATIONS': 'certifications',
        'TYPE_PROJECTS': 'projects',
        'TYPE_SKILLS': 'skills',
      };

      const reorderPayload: Array<{ section_uid: string; index: number }> = [];
      
      newSections.forEach((section, index) => {
        if (section.isCustom) {
        } else {
          const structure = structures.find(s => structureTypeToId[s.structure_type] === section.id);
          if (structure && structure.uid) {
            reorderPayload.push({
              section_uid: structure.uid,
              index: index,
            });
          }
        }
      });

      if (reorderPayload.length > 0) {
        try {
          await onReorderSections(reorderPayload);
        } catch (error) {
        }
      }
    }

    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleEdit = (section: Section) => {
    setEditingSectionId(section.id);
    setEditingText(section.title);
  };

  const handleSaveEdit = (sectionId: string) => {
    if (editingText.trim()) {
      const section = displaySections.find(s => s.id === sectionId);
      
      if (section?.isCustom) {
        if (onUpdateCustomSectionTitle) {
          onUpdateCustomSectionTitle(sectionId, editingText.trim());
        }
        if (localSectionOrder !== null) {
          setLocalSectionOrder(localSectionOrder.map(s => 
            s.id === sectionId ? { ...s, title: editingText.trim() } : s
          ));
        }
      } else {
        const structureTypeToId: Record<string, string> = {
          'TYPE_INFO': 'about',
          'TYPE_WORK_EXPERIENCE': 'experience',
          'TYPE_VOLUNTEER_EXPERIENCE': 'volunteer',
          'TYPE_EDUCATION': 'education',
          'TYPE_CERTIFICATIONS': 'certifications',
          'TYPE_PROJECTS': 'projects',
          'TYPE_SKILLS': 'skills',
        };
        
        const structure = structures.find(s => structureTypeToId[s.structure_type] === sectionId);
        const sectionUid = structure?.uid;
        
        if (sectionUid && onUpdateSectionTitle) {
          const requestPayload = {
            section_uid: sectionUid,
            new_title: editingText.trim()
          };
          onUpdateSectionTitle(sectionUid, editingText.trim());
        } else {
        }
        
        const updatedSections = unlockedSections.map(s => 
          s.id === sectionId ? { ...s, title: editingText.trim() } : s
        );
        setUnlockedSections(updatedSections);
        
        if (localSectionOrder !== null) {
          setLocalSectionOrder(localSectionOrder.map(s => 
            s.id === sectionId ? { ...s, title: editingText.trim() } : s
          ));
        }
        
        if (onUpdateRegularSectionTitle) {
          onUpdateRegularSectionTitle(sectionId, editingText.trim());
        }
      }
    }
    setEditingSectionId(null);
    setEditingText('');
  };

  const handleCancelEdit = () => {
    setEditingSectionId(null);
    setEditingText('');
  };

  return (
    <div className="resume-editor__section-expanded-body">
      <div className="section-order__locked-section">
        <div className="section-order__header">
          <div className="section-order__header-icon-wrapper">
            <div className="section-order__header-icon">
              <CircleHelp size={16} />
            </div>
            <div className="section-order__header-tooltip">
              The template you selected either locks these sections in one area or includes the information in the resume header (e.g. Links or contact info being included in the header).
            </div>
          </div>
          <h3 className="section-order__header-title">Locked Sections for Template</h3>
        </div>
        <div className="section-order__locked-list">
          {lockedSections.map((section) => (
            <div key={section.id} className="section-order__item section-order__item--locked">
              <div className="section-order__item-icon section-order__item-icon--lock">
                <Lock size={14} />
              </div>
              <span className="section-order__item-text">{section.title}</span>
              <div className="section-order__item-drag-handle">
                <span></span>
                <span></span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="section-order__unlocked-section">
        <div className="section-order__unlocked-list">
          {displaySections.map((section, index) => (
            <div
              key={section.id}
              className={`section-order__item section-order__item--unlocked ${
                draggedIndex === index ? 'section-order__item--dragging' : ''
              } ${
                dragOverIndex === index ? 'section-order__item--drag-over' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              {editingSectionId === section.id ? (
                <input
                  type="text"
                  className="section-order__item-input"
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onBlur={() => handleSaveEdit(section.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveEdit(section.id);
                    } else if (e.key === 'Escape') {
                      handleCancelEdit();
                    }
                  }}
                  autoFocus
                />
              ) : (
                <div 
                  className="section-order__item-content"
                  onClick={() => handleEdit(section)}
                >
                  <button
                    className="section-order__item-icon section-order__item-icon--edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(section);
                    }}
                    title="Edit section title"
                  >
                    <SquarePen size={14} />
                  </button>
                  <span className="section-order__item-text">{section.title}</span>
                </div>
              )}
              <div 
                className="section-order__item-drag-handle"
                onClick={(e) => e.stopPropagation()}
              >
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
